import React, { useEffect, useState } from 'react';
import Link from 'next/link';

import Location from '@/assets/icons/location.svg';

import { pageIsService } from '../../../helpers/list-page/type-guards';
import { calculatePrefixPath } from '../../../helpers/rendered-page-creation/calculate-prefixed-path';
import { IMeetingPlacePageTenantShopItem } from '@/lib/fragments/meetingPlacePageTenantShopItem';
import { IMeetingPlacePageTenantEntertainmentItem } from '@/lib/fragments/meetingPlacePageTenantEntertainmentItem';
import { IMeetingPlacePageTenantFoodBevItem } from '@/lib/fragments/meetingPlacePageTenantFoodBevItem';
import { IMeetingPlacePageServiceItem } from '@/lib/fragments/meetingPlacePageServiceItem';
import { IMeetingplacePageHomeMinifiedExtendedOpeningHours } from '@/lib/fragments/meetingPlacePageHome-minified';
import {
  useGetLibraryConstantsDictionaryData,
  useTenantConfig,
} from '@/providers/ConfigProvider';

interface ViewOnMapProps {
  page:
  | IMeetingPlacePageTenantShopItem
  | IMeetingPlacePageTenantEntertainmentItem
  | IMeetingPlacePageTenantFoodBevItem
  | IMeetingPlacePageServiceItem;
  homepage: IMeetingplacePageHomeMinifiedExtendedOpeningHours;
  className?: string;
}

const ViewOnMap = ({ page, homepage, className }: ViewOnMapProps) => {
  const config = useTenantConfig();
  const prefix = config?.GATSBY_SITE_PATH_PREFIX;
  const pageName = pageIsService(page)
    ? (page?.name ?? page?.serviceType?.name)
    : (page?.name ?? page?.brand?.name);
  const dictionary = useGetLibraryConstantsDictionaryData();

  const [floors, setFloors] = useState<string[]>([]);
  const [numOfFloors, setNumOfFloors] = useState<number>(0);
  const [levelIndexObject, setLevelIndexObject] = useState<{
    [key: string]: number;
  }>({});

  const fetchDatafromApi = async (url: string) => {
    const response = await fetch(url);
    const data = await response.json();
    return data;
  };

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        if (homepage?.meetingPlace?.visioGlobeMapKey && page?.sys?.locale) {
          const mapUrl = `https://mapserver.visioglobe.com/${homepage.meetingPlace.visioGlobeMapKey}/map.json`;
          const data = await fetchDatafromApi(mapUrl);
          const filteredPlaces = data?.pois?.pois?.filter((place: any) =>
            page?.floorPlanIDs?.map((id) => id.trim()).includes(place.poiID) ||
            page?.unitId?.map((id) => id.split("-").slice(1).join("-").trim()).includes(place.poiID),
          );

          const floorsOfPlace: string[] = [];
          filteredPlaces?.forEach((place: any) => {
            if (place.floorName && !floorsOfPlace.includes(place.floorName)) {
              floorsOfPlace.push(place.floorName);
            }
          });
          setFloors(floorsOfPlace);

          const defaultBuilding =
            data?.extra_data?.config?.venue_layout?.defaultBuilding;
          const buildingfloors =
            data?.extra_data?.config?.venue_layout?.buildings[defaultBuilding]
              ?.floors;
          if (buildingfloors) {
            let updatedLevelIndexObject = {};
            for (const floor in buildingfloors) {
              setNumOfFloors((prevVal) => prevVal + 1);
              updatedLevelIndexObject = {
                ...updatedLevelIndexObject,
                [floor]: buildingfloors[floor].levelIndex,
              };
            }
            setLevelIndexObject(updatedLevelIndexObject);
          }
        }
      } catch (error) {
        console.error('Error fetching or processing map data:', error);
      }
    };

    fetchMapData();
  }, [
    homepage.meetingPlace.visioGlobeMapKey,
    page?.sys?.locale,
    page?.floorPlanIDs,
    page?.unitId
  ]);

  const getFloorNumber = (inputString: string) => {
    // Regular expression to match "UL" or "LL" followed by a number
    const match = inputString.match(/(UL|LL)(\d+)/);

    if (match) {
      const [, prefix, floor] = match;

      if (prefix === 'LL') {
        const floorNumber = parseInt(floor, 10);
        return floorNumber === 0 ? '0' : `-${floorNumber}`;
      } else if (prefix === 'UL') {
        return floor;
      }
    }

    return '';
  };

  return (
    <div
      className={`mb-4 mt-8 flex flex-wrap gap-y-4 xs:flex-nowrap items-center rounded-xl bg-background-primary-light p-4 lg:bg-background-primary ${className}`}
    >
      <Location className="mr-4 w-10 min-w-[40px]" />
      <div className="mr-4">
        <h2 className="text-headingxs text-text-default">{pageName}</h2>
        {numOfFloors > 1 && floors.length > 0 && (
          <div className="text-bodys">
            <span className="mr-1">{dictionary?.floor}</span>
            {floors
              .sort()
              .map((floor, index) => {
                if (floor === '' || floor === 'outside') {
                  return dictionary?.outside;
                } else {
                  const includesFloor =
                    Object.keys(levelIndexObject).includes(floor);
                  const level = includesFloor
                    ? getFloorNumber(floor)
                    : floor.slice(-1);
                  return `${index > 0 ? ', ' : ''}${level}`;
                }
              })
              .filter((val, index, self) => self.indexOf(val) === index)}
          </div>
        )}
      </div>

      <Link
        href={`${calculatePrefixPath(
          `/${page?.sys?.locale}/${homepage.meetingPlace?.visioGlobeFloorPlanPage?.slug}?id=${page?.sys.id}`,
          prefix,
        )}`}
        className="ml-0 xs:ml-auto rounded-xl bg-background-secondary px-6 py-2 text-headingxs text-text-default lg:max-w-[35%]"
      >
        {dictionary?.viewOnMap}
      </Link>
    </div>
  );
};

export default ViewOnMap;
